import { findItem, QUALITIES, type Quality, TOWNS, type TownId } from '@wyn/content';
import { balance } from './config/balance.ts';
import { assertGame, GameError } from './errors.ts';
import { assertCan } from './guards.ts';
import { type PlayerState, townName } from './player.ts';
import { assertChatOn } from './block.ts';
import { assertMayChat } from './sanction.ts';
import { assertWelcomeChat, maskSwearing } from './words.ts';

/**
 * What a socket can follow. `realm` and `town:<id>` are also the chat channels; a town topic also
 * carries who comes and goes. `book:<item>:<quality>` carries changes to one good's order book.
 */
export type Topic = { kind: 'realm' } | { kind: 'town'; town: TownId } | { kind: 'book'; itemKey: string; quality: Quality };
export type ChatChannel = 'realm' | `town:${TownId}`;

export function parseTopic(raw: string): Topic | null {
  if (raw === 'realm') return { kind: 'realm' };
  const [kind, a, b, ...rest] = raw.split(':');
  if (rest.length > 0) return null;
  if (kind === 'town' && b === undefined && (TOWNS as readonly string[]).includes(a ?? '')) return { kind: 'town', town: a as TownId };
  if (kind === 'book' && a && findItem(a) && (QUALITIES as readonly string[]).includes(b ?? '')) return { kind: 'book', itemKey: a, quality: b as Quality };
  return null;
}

export function topicName(t: Topic): string {
  switch (t.kind) {
    case 'realm': return 'realm';
    case 'town': return `town:${t.town}`;
    case 'book': return `book:${t.itemKey}:${t.quality}`;
  }
}

export const townTopic = (town: TownId) => `town:${town}` as const;
export const bookTopic = (itemKey: string, quality: Quality) => `book:${itemKey}:${quality}`;

/** A town's talk and comings and goings are for those in it: not on the road, and not from elsewhere. */
function assertInTown(p: PlayerState, town: TownId) {
  assertGame(p.status.kind !== 'travelling', 'BLOCKED_BY_STATE', 'You are on the road.');
  assertGame(p.town === town, 'WRONG_PLACE', `You are not in ${townName(town)}.`);
}

/** Throws unless the (refreshed) player may follow the topic. Books and the realm are open to all. */
export function assertMayFollow(p: PlayerState, topic: Topic): void {
  if (topic.kind === 'town') assertInTown(p, topic.town);
}

/**
 * The topics a player may follow, out of those asked for. Unknown or forbidden ones are dropped
 * rather than failing the lot, so a socket that lags behind a journey keeps the rest.
 */
export function followable(p: PlayerState, requested: readonly string[]): string[] {
  const out = new Set<string>();
  for (const raw of requested) {
    const t = parseTopic(raw);
    if (!t) continue;
    try {
      assertMayFollow(p, t);
    } catch (e) {
      if (e instanceof GameError) continue;
      throw e;
    }
    out.add(topicName(t));
    if (out.size >= balance.realtime.maxTopics) break;
  }
  return [...out];
}

export function parseChannel(raw: string): ChatChannel | null {
  const t = parseTopic(raw);
  return t && t.kind !== 'book' ? (topicName(t) as ChatChannel) : null;
}

/** Collapses whitespace and strips control characters. Empty or over-long lines are refused. */
export function normaliseChat(raw: string): string {
  const text = raw.replace(/\p{Cc}+/gu, ' ').replace(/\s+/g, ' ').trim();
  assertGame(text.length > 0, 'INVALID', 'Say something.');
  assertGame(text.length <= balance.realtime.chatMaxLength, 'INVALID', `Keep it under ${balance.realtime.chatMaxLength} characters.`);
  return text;
}

export interface ChatMessage {
  id: string;
  channel: ChatChannel;
  playerId: string;
  name: string;
  text: string;
  at: number;
  /** A steward took the line down; its text is gone (the report that led to it keeps a copy). */
  removed?: true;
}

/** A line of chat. Chat is allowed in every state (gaol included); a town's channel needs you in the town, and you need chat on. */
export function speak(p: PlayerState, channelRaw: string, raw: string, now: number, id: string): ChatMessage {
  const channel = parseChannel(channelRaw);
  assertGame(channel !== null, 'INVALID', 'No such channel.');
  assertCan(p, 'chat');
  assertMayChat(p, now);
  assertChatOn(p);
  const t = parseTopic(channel)!;
  if (t.kind === 'town') assertInTown(p, t.town);
  const text = normaliseChat(raw);
  assertWelcomeChat(text);
  return { id, channel, playerId: p.id, name: p.name, text: maskSwearing(text), at: now };
}
